import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

// components
import { Pagination, SortChangedEvent, Table } from "@performyard/py-components";

// utils
import { getCompanyFeedbackColumns } from "./components/CompanyFeedbackCols";
import CompanyFeedbackFilters from "./components/CompanyFeedbackFilters";
import { CompanyFeedbackFilter } from "./components/CompanyFeedbackFilters";
import { DownloadCompanyFeedbackCsvButton } from "./components/DownloadCompanyFeedbackCsvButton";
import ReadableCompanyFeedbackFilterChips from "./components/ReadableCompanyFeedbackFilterChips";

// hooks
import { useSearchService, useSearchServicePrefetch } from "../../api/hooks";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import { SERVICE_FEEDBACK_COMPANY } from "../../api/services/defs/search-service";

import { getUserIsAdmin, getUserIsBasicAdmin } from "../../selectors/app";

import { Feedback } from "../UserFeedback/utils";
import { getFiltersForSearch } from "./utils";

import InfoPageTemplate from "../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
//pageTemplate
import useInfoPageTemplateQueryState from "../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../Error";

const sideNavigationItems = [{ key: "ALL_FEEDBACK", label: "All Feedback", href: "/company/feedback" }];

const LIMIT = 10;

const CompanyFeedback = () => {
	const userIsAdmin = useSelector(getUserIsAdmin);
	const userIsBasicAdmin = useSelector(getUserIsBasicAdmin);
	const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
	const { value: feedbackFeatureVisible } = useFeatureFlagQuery("ff-is-feedback-enabled", true);

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<CompanyFeedbackFilter>({
		defaultSortName: "last_updated",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	const { data, isLoading } = useSearchService<Feedback>({
		service: SERVICE_FEEDBACK_COMPANY,
		filters: getFiltersForSearch(debouncedSearchValue, filters),
		skip: (pageNumber - 1) * LIMIT,
		limit: LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Feedbacks",
	});

	// prefetch page before and 3 pages after
	useSearchServicePrefetch<Feedback>(
		{
			service: SERVICE_FEEDBACK_COMPANY,
			filters: getFiltersForSearch(debouncedSearchValue, filters),
			skip: (pageNumber - 2) * LIMIT,
			limit: LIMIT,
			sort,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<Feedback>(
		{
			service: SERVICE_FEEDBACK_COMPANY,
			filters: getFiltersForSearch(debouncedSearchValue, filters),
			skip: pageNumber * LIMIT,
			limit: LIMIT,
			sort,
		},
		3
	);

	const feedbacks = data?.results || [];
	const total = data?.total || 0;

	const columnDefs = useMemo(() => getCompanyFeedbackColumns(), []);

	const onSortChanged = (event: SortChangedEvent<Feedback>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			setSort({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	if (!(userIsAdmin || userIsBasicAdmin)) {
		return <ErrorPage code={403} />;
	}

	if (!feedbackFeatureVisible) {
		return <ErrorPage code={404} />;
	}

	const mainContent = (
		<div>
			<Table<Feedback>
				autoHeight
				rowData={feedbacks}
				columnDefs={columnDefs}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Feedback Found"
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction as "asc" | "desc" }] : [],
					},
				}}
				animateRows={false}
			/>
			{total > 0 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(total / LIMIT)}
						onPageChange={(newPage: number) => setPageNumber(newPage)}
					/>
				</div>
			)}
		</div>
	);

	const filterDrawer = (
		<CompanyFeedbackFilters
			isOpen={isFilterDrawerOpen}
			onClose={() => setIsFilterDrawerOpen(false)}
			filters={filters}
			onChange={setFilters}
		/>
	);

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			searchPlaceholder="Search Feedback..."
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => setIsFilterDrawerOpen(true)}
		/>
	);

	const actionButtons = [<DownloadCompanyFeedbackCsvButton key="download" />];

	return (
		<InfoPageTemplate
			mainContent={mainContent}
			pageTitle="Company Feedback"
			navigationLinks={sideNavigationItems}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={<ReadableCompanyFeedbackFilterChips filters={filters} setFilters={setFilters} />}
			drawer={filterDrawer}
			actionButtons={actionButtons}
			activeNavigationLinkKey={"ALL_FEEDBACK"}
		/>
	);
};

export default CompanyFeedback;
